/**
 * Channel Relationship Store — in-memory history + dismissal log for marketing
 * partner recommendations surfaced on the Command Dashboard.
 *
 * Mirrors the homeowner interaction model: every recommendation can be
 * Applied (decision captured), Dismissed (with reason + optional snooze),
 * or Logged (touch / note added to history). All entries persist to a
 * module-scoped store with subscription so both dashboard skins stay in sync.
 *
 * Beta-safe: no Supabase, no destructive ops. Snooze suppresses re-surfacing
 * without deleting anything; everything stays auditable in the timeline.
 */

export type ChannelEventType =
  | "decision_applied"   // operator applied the recommendation (e.g. budget increase)
  | "dismissal"          // operator dismissed the recommendation (with reason)
  | "touch"              // logged a contact / note without applying
  | "performance_note";  // freeform observation

export interface ChannelEvent {
  id: string;
  channelId: string;        // e.g. "meta-growth-engine"
  channelName: string;      // human-readable
  eventType: ChannelEventType;
  recommendationKey?: string; // stable key tying event back to the surfaced rec
  recommendationText?: string;
  decisionLabel?: string;   // e.g. "Increase budget"
  notes: string;
  contact?: string;         // who at the partner (account rep name)
  amount?: number;          // optional dollar amount (budget changes)
  effectiveDate?: string;   // ISO — when the change takes effect
  dismissReason?: string;
  snoozeUntil?: string;     // ISO — recs with this key suppressed until then
  actor: string;            // operator who logged it
  timestamp: string;        // ISO
}

type Listener = () => void;

const events: ChannelEvent[] = [];
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((l) => l());

const genId = () =>
  `che_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export function addChannelEvent(
  partial: Omit<ChannelEvent, "id" | "timestamp"> & { timestamp?: string },
): ChannelEvent {
  const event: ChannelEvent = {
    id: genId(),
    timestamp: partial.timestamp ?? new Date().toISOString(),
    ...partial,
  };
  events.unshift(event);
  notify();
  return event;
}

export function getChannelEvents(channelId?: string): ChannelEvent[] {
  return channelId ? events.filter((e) => e.channelId === channelId) : [...events];
}

/**
 * Returns the active dismissal (if any) for a given recommendation key.
 * A dismissal is "active" if its snoozeUntil is in the future, or if
 * snoozeUntil is undefined (= dismissed indefinitely).
 */
export function getActiveDismissal(recommendationKey: string): ChannelEvent | null {
  const now = Date.now();
  const dismissals = events.filter(
    (e) => e.eventType === "dismissal" && e.recommendationKey === recommendationKey,
  );
  for (const d of dismissals) {
    if (!d.snoozeUntil) return d;
    if (new Date(d.snoozeUntil).getTime() > now) return d;
  }
  return null;
}

export function isRecommendationDismissed(recommendationKey: string): boolean {
  return getActiveDismissal(recommendationKey) !== null;
}

/**
 * Returns every currently-active dismissal across all recommendations,
 * de-duped to the latest active dismissal per recommendation key. Used by
 * the Dismissed Recommendations drawer so operators can recover from
 * accidental dismissals.
 */
export function getActiveDismissals(): ChannelEvent[] {
  const seen = new Set<string>();
  const out: ChannelEvent[] = [];
  // events is unshift-ordered (newest first), so the first match per key wins.
  for (const e of events) {
    if (e.eventType !== "dismissal" || !e.recommendationKey) continue;
    if (seen.has(e.recommendationKey)) continue;
    if (!e.snoozeUntil || new Date(e.snoozeUntil).getTime() > Date.now()) {
      seen.add(e.recommendationKey);
      out.push(e);
    }
  }
  return out;
}

/** Re-surface a previously dismissed recommendation by adding a touch entry. */
export function clearDismissal(recommendationKey: string, actor: string): void {
  // We don't mutate the dismissal — we add a forward event that supersedes it.
  // A simple approach: insert a fresh dismissal with snoozeUntil in the past.
  const past = new Date(Date.now() - 1000).toISOString();
  events.unshift({
    id: genId(),
    channelId: "__system__",
    channelName: "System",
    eventType: "touch",
    recommendationKey,
    notes: "Dismissal cleared — recommendation re-surfaced",
    actor,
    timestamp: new Date().toISOString(),
  });
  // Mark prior dismissals as expired by adding a snooze-expired marker.
  events.forEach((e) => {
    if (
      e.eventType === "dismissal" &&
      e.recommendationKey === recommendationKey &&
      (!e.snoozeUntil || new Date(e.snoozeUntil).getTime() > Date.now())
    ) {
      e.snoozeUntil = past;
    }
  });
  notify();
}

/** React hook — subscribes to store changes and re-renders consumers. */
import { useSyncExternalStore } from "react";

export function useChannelEvents(channelId?: string): ChannelEvent[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => (channelId ? events.filter((e) => e.channelId === channelId) : events),
    () => (channelId ? events.filter((e) => e.channelId === channelId) : events),
  );
}

/** Hook variant that also exposes dismissal status for a rec key. */
export function useRecommendationDismissed(recommendationKey: string): boolean {
  const all = useChannelEvents();
  // Recompute against snapshot
  void all; // dependency for subscription
  return isRecommendationDismissed(recommendationKey);
}
